'use client';

import React, { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircle, faList } from '@fortawesome/pro-solid-svg-icons';
import {
  faClipboardList,
  faArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Card,
  Checkbox,
  PulseButton,
  Tooltip,
} from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { AddToListModal } from 'src/components';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useLogging } from 'src/context/logging';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import { useGetProviderStrategyTabsQuery } from 'src/api/redux';
import {
  useGetStrategyMetricsQuery,
  useGetEngageStrategyListMembersPaginatedInfiniteQuery,
  useGetEngageStrategyDetailsQuery,
} from 'src/api/redux/strategy';

import { useInfiniteScroll } from 'src/hooks/common/useInfiniteScroll';
import { EngageStrategyMetricsHeader } from 'src/components/Strategy/Profiles/components';
import { STRATEGY_COLORS } from 'src/components/Strategy/Profiles';

import { STRATEGY_TYPES } from 'src/components/Strategy/constants';
import { useEngageStrategyConnector } from '../hooks/useEngageStrategyConnector';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { getProfileUrl } from 'src/utils/profileUrl';
import type { AddToListResult } from 'src/components/AddToListModal/types';

const PAGE_SIZE = 25;

const CompactEngageStrategy: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const log = useLogging();
  const { closeDrawer } = useDrawer();
  const router = useRouter();
  const engageStrategy = widgetConfig?.widget?.engage_strategy;
  const hasExplicitDateRange =
    widgetConfig?.widget?.min_year != null ||
    widgetConfig?.widget?.max_year != null;
  const strategyId = engageStrategy?.id?.toString() ?? '';
  const strategyType = engageStrategy?.type ?? '';
  const isPatientJourney = strategyType === STRATEGY_TYPES.PATIENT_JOURNEY;
  const { providerId: rawProviderId, profileType } = getEntityIdentifiers(entityDetails);
  const providerId = rawProviderId?.toString() || '';

  const {
    connectorData,
    isConnectorLoading,
    isConnectorError,
    isConnector404,
    isConnectorExecuting,
    isProcessing,
    isExecuting,
    handleExecuteStrategy,
  } = useEngageStrategyConnector({ strategyId, providerId });

  const { data: engageStrategyDetails } = useGetEngageStrategyDetailsQuery(
    strategyId,
    { skip: !strategyId }
  );

  const [selectedProviders, setSelectedProviders] = useState<any[]>([]);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);

  const skipDataQueries = !strategyId || isConnector404 || isConnectorExecuting;

  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    { strategyType: 'engage', strategyId },
    { skip: skipDataQueries }
  );

  const sortMetric = useMemo(() => {
    if (!strategyMetrics?.results?.length) return null;
    return strategyMetrics.results[0]?.display_options?.[0]?.code || null;
  }, [strategyMetrics?.results]);

  const {
    data: paginatedData,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetEngageStrategyListMembersPaginatedInfiniteQuery(
    {
      strategyId,
      provider_id: providerId,
      page_size: PAGE_SIZE,
      sort: sortMetric ? 'metric' : 'date_added',
      order: 'desc',
      ...(sortMetric && { sort_metric: sortMetric }),
    },
    { skip: !strategyId || !providerId || skipDataQueries }
  );

  const members = useMemo(
    () => paginatedData?.pages?.flatMap((page: any) => page.results) ?? [],
    [paginatedData]
  );

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || !!isFetchingNextPage,
    fetchNextPage,
  });

  const { hasPermission } = useAuthorization();
  const { data: strategyTabsResponse } = useGetProviderStrategyTabsQuery(
    { providerId },
    { skip: !providerId || !hasPermission(TARGET_STRATEGIES_READ) }
  );

  const chainId = useMemo(() => {
    if (!strategyTabsResponse?.results || !engageStrategy?.id) return null;
    const chain = strategyTabsResponse.results.find((c: any) =>
      c.engage_strategies?.some((es: any) => es.id === engageStrategy.id)
    );
    return chain?.id ?? null;
  }, [strategyTabsResponse?.results, engageStrategy?.id]);

  const currentPageSelected = useMemo(() => {
    if (members.length === 0) return false;
    return members.every((member: any) =>
      selectedProviders.some((p: any) => p.provider_id === member.provider_id)
    );
  }, [members, selectedProviders]);

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;

      if (isChecked) {
        log.event('strategyMultiSelectModeActivated');
        const newSelections = [...selectedProviders];
        members.forEach((member: any) => {
          if (
            !selectedProviders.some(
              (p: any) => p.provider_id === member.provider_id
            )
          ) {
            newSelections.push(member);
          }
        });
        setSelectedProviders(newSelections);
      } else {
        setSelectedProviders(
          selectedProviders.filter(
            (p: any) =>
              !members.some((m: any) => m.provider_id === p.provider_id)
          )
        );
      }
    },
    [members, log, selectedProviders]
  );

  const handleCheckboxToggle = useCallback(
    (member: any) => {
      const isSelected = selectedProviders.some(
        (p: any) => p.provider_id === member.provider_id
      );
      if (isSelected) {
        setSelectedProviders(
          selectedProviders.filter(
            (p: any) => p.provider_id !== member.provider_id
          )
        );
      } else {
        setSelectedProviders([...selectedProviders, member]);
      }
    },
    [selectedProviders]
  );

  const handleAddToListSuccess = useCallback(
    (result: AddToListResult) => {
      log.event('Providers added to list', {
        listName: result.targetListName,
        count: result.addedCount,
      });
      setSelectedProviders([]);
    },
    [log]
  );

  const handleMemberClick = useCallback(
    (member: any) => {
      const memberType = member.type || 'hcp';
      const memberId = member.provider_id || member.id;
      router.push(getProfileUrl(memberType, memberId));
      closeDrawer('profile');
    },
    [router, closeDrawer]
  );

  const selectedCount = selectedProviders.length;

  const isAnyLoading = isConnectorLoading || isLoading;
  const processingMessage =
    connectorData?.status === 'CREATED'
      ? 'Strategy queued...'
      : 'Running strategy...';
  const totalCount = connectorData?.list_items_count ?? 0;
  const emptyMessage =
    strategyType === STRATEGY_TYPES.PATIENT_JOURNEY
      ? 'No insights found'
      : 'No providers found';

  const showMetricsHeader =
    strategyType === STRATEGY_TYPES.TREATING_VOLUME;

  const showPatientJourneyMetrics =
    isPatientJourney &&
    !isProcessing &&
    !isAnyLoading &&
    !isConnectorError &&
    !isError &&
    members.length > 0;

  if (!engageStrategy?.id) return null;

  return (
    <Card
      className="p-4 flex flex-col gap-4 bg-white h-full"
      data-testid={`compact-engage-strategy-${engageStrategy.id}`}
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <h3
            className="text-lg font-bold text-primary truncate"
            data-testid={`compact-engage-strategy-title-${engageStrategy.id}`}
          >
            {widgetConfig?.widget?.label ?? engageStrategy.label}
          </h3>
          {hasExplicitDateRange && widgetConfig?.dateRangeLabel && (
            <>
              <Icon
                icon={faCircle}
                className="text-neutral-300 !h-2 !w-2 flex-shrink-0"
              />
              <span
                className="text-xs text-neutral-500 flex-shrink-0"
                data-testid="compact-engage-strategy-date-range"
              >
                {widgetConfig.dateRangeLabel}
              </span>
            </>
          )}
        </div>
        {(widgetConfig?.widget?.description ||
          engageStrategyDetails?.description) && (
          <p
            className="text-xs text-secondary leading-snug"
            data-testid="compact-engage-strategy-description"
          >
            {widgetConfig?.widget?.description ??
              engageStrategyDetails?.description}
          </p>
        )}
        {connectorData?.last_refreshed && (
          <p
            className="text-xs text-neutral-400"
            data-testid="compact-engage-strategy-last-refreshed"
          >
            Last run{' '}
            <time dateTime={connectorData.last_refreshed}>
              {new Date(connectorData.last_refreshed).toLocaleDateString(
                undefined,
                { month: 'short', day: 'numeric', year: 'numeric' }
              )}
            </time>
          </p>
        )}
      </div>

      {showPatientJourneyMetrics && (
        <EngageStrategyMetricsHeader
          engageStrategyId={strategyId}
          color={
            STRATEGY_COLORS[strategyType as keyof typeof STRATEGY_COLORS] ||
            'text-pink-600'
          }
          compact
        />
      )}

      <div className="flex-1 min-h-0 max-h-80 overflow-x-auto overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-3xl">
        {isProcessing ? (
          <div
            className="flex flex-col items-center justify-center h-full min-h-48 gap-4"
            role="status"
            aria-label="Strategy is processing"
            data-testid="compact-engage-strategy-executing"
          >
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:300ms]" />
            </div>
            <p className="text-sm font-medium text-neutral-500">
              {processingMessage}
            </p>
          </div>
        ) : isAnyLoading ? (
          <WidgetLoading data-testid="compact-engage-strategy-loading" />
        ) : isConnector404 ? (
          <div
            className="flex flex-col items-center justify-center h-full min-h-48 gap-4"
            role="status"
            data-testid="compact-engage-strategy-not-run"
          >
            <div
              className="w-16 h-16 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl flex items-center justify-center"
              aria-hidden="true"
            >
              <Icon
                icon={faClipboardList}
                className="text-neutral-400"
                size="lg"
              />
            </div>
            <p className="text-sm text-neutral-500">
              This strategy has not been run for this provider yet.
            </p>
            <PulseButton
              variant="solid"
              color="primary"
              onClick={handleExecuteStrategy}
              loading={isExecuting}
              aria-label="Execute engage strategy"
              className="whitespace-nowrap"
              data-testid="compact-engage-strategy-execute-button"
            >
              Execute Engage Strategy
            </PulseButton>
          </div>
        ) : isError ? (
          <div
            className="flex items-center justify-center h-full min-h-48"
            role="alert"
            data-testid="compact-engage-strategy-error"
          >
            <p className="text-sm text-neutral-500">
              Unable to load strategy results. Please contact your Customer
              Success team member for assistance.
            </p>
          </div>
        ) : !members.length ? (
          <div
            className="flex flex-col items-center justify-center h-full min-h-48 gap-4"
            role="status"
            data-testid="compact-engage-strategy-empty"
          >
            <div
              className="w-16 h-16 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl flex items-center justify-center"
              aria-hidden="true"
            >
              <Icon
                icon={faClipboardList}
                className="text-neutral-400"
                size="lg"
              />
            </div>
            <p className="text-sm font-semibold text-neutral-500">
              {emptyMessage}
            </p>
          </div>
        ) : (
          <>
            {showMetricsHeader && (
              <EngageStrategyMetricsHeader
                engageStrategyId={strategyId}
                color={
                  STRATEGY_COLORS[strategyType as keyof typeof STRATEGY_COLORS] ||
                  'text-pink-600'
                }
              />
            )}

            <div
              className="min-w-max"
              data-testid="compact-engage-strategy-scroll-container"
            >
            <div
              className="flex items-center justify-between gap-2 pb-1 border-b border-neutral-100 sticky top-0 bg-white z-20"
              data-testid="compact-engage-strategy-selection-bar"
            >
              <div className="flex items-center gap-2 w-48 min-w-48 sticky left-0 bg-white z-20 pr-4 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    size="sm"
                    variant="solid"
                    color="primary"
                    checked={currentPageSelected}
                    onChange={handleSelectAll}
                    aria-label={
                      currentPageSelected ? 'Deselect all' : 'Select all'
                    }
                    data-testid="compact-engage-strategy-select-all"
                  />
                  <span className="text-xs font-medium text-primary leading-none whitespace-nowrap">
                    Select all
                  </span>
                </label>
                <PulseButton
                  variant="plain"
                  size="xs"
                  onClick={() => setShowAddToListDialog(true)}
                  disabled={selectedCount === 0}
                  className="whitespace-nowrap"
                  data-testid="compact-engage-strategy-add-to-list-button"
                >
                  <Icon icon={faList} className="w-3 h-3" />
                  Add to List
                </PulseButton>
              </div>
              <div className="flex items-center">
                {strategyMetrics?.results?.map((metric: any) => (
                  <Tooltip
                    key={metric.id}
                    content={metric.label}
                    position="top"
                  >
                    <span
                      className="text-xs font-semibold text-tertiary w-24 text-end px-2 truncate block"
                      data-testid={`compact-engage-strategy-metric-header-${metric.id}`}
                    >
                      {metric.label}
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>
            <div
              className="flex flex-col"
              role="list"
              aria-label={`${engageStrategy.label} members`}
              data-testid="compact-engage-strategy-members-list"
            >
              {members.map((member: any) => (
                  <div
                    key={member.id || member.provider_id}
                    className="group flex items-center justify-between gap-2 py-3 border-b border-neutral-100 last:border-b-0 cursor-pointer hover:bg-neutral-50 transition-colors"
                    role="listitem"
                    onClick={() => handleMemberClick(member)}
                    data-testid={`compact-engage-member-${
                      member.provider_id || member.id
                    }`}
                  >
                    <div className="flex items-center gap-2 w-48 min-w-48 sticky left-0 bg-white group-hover:bg-neutral-50 transition-colors z-0 pr-4 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0"
                      >
                        <Checkbox
                          checked={selectedProviders.some(
                            (p: any) => p.provider_id === member.provider_id
                          )}
                          onChange={() => handleCheckboxToggle(member)}
                          size="sm"
                          variant="solid"
                          color="primary"
                          data-testid={`compact-engage-member-checkbox-${
                            member.provider_id || member.id
                          }`}
                          aria-label={`Select ${member.name}`}
                        />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-bold text-link truncate">
                          {member.name}
                        </span>
                        {member.specialty && (
                          <span className="text-xs text-secondary truncate">
                            {member.specialty}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center flex-shrink-0">
                      {strategyMetrics?.results?.map((metric: any) => {
                        const optionCode = metric.display_options?.[0]?.code;
                        const rawValue = optionCode
                          ? member.metrics_v2?.[metric.id]?.[optionCode]
                          : undefined;
                        const displayValue =
                          rawValue != null ? rawValue.toLocaleString() : '-';

                        return (
                          <div
                            key={metric.id}
                            className="flex items-center justify-end px-2 py-1 w-24"
                            data-testid={`compact-engage-member-metric-${
                              member.provider_id || member.id
                            }-${metric.id}`}
                          >
                            <span className="text-sm font-semibold text-teal-900 tabular-nums">
                              {displayValue}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
            <div
              ref={fetchTriggerRef}
              className="h-1"
              aria-hidden="true"
              data-testid="compact-engage-strategy-scroll-trigger"
            />
            {isFetchingNextPage && (
              <WidgetLoading />
            )}
          </div>

          <AddToListModal
            isOpen={showAddToListDialog}
            onClose={() => setShowAddToListDialog(false)}
            mode="add"
            providers={selectedProviders}
            onSuccess={handleAddToListSuccess}
          />
        </>
        )}
      </div>

      {chainId && (
        <Link
          href={`/profiles/${profileType}/${providerId}/strategy/${chainId}`}
          onClick={() => closeDrawer('profile')}
          className="flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
          data-testid="compact-engage-strategy-view-link"
        >
          {totalCount > 0
            ? `View all ${totalCount.toLocaleString()} providers`
            : 'View strategy'}
          <Icon
            icon={faArrowRight}
            className="h-3 w-3"
            aria-hidden="true"
          />
        </Link>
      )}
    </Card>
  );
};

export default CompactEngageStrategy;
